import TrendingDownOutlinedIcon from '@mui/icons-material/TrendingDownOutlined';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import { Project } from '../models/Models';
import SolarPowerIcon from '@mui/icons-material/SolarPower';
import CloudIcon from '@mui/icons-material/Cloud';
import { InfoAlert } from '../components/common/InfoAlert';
import { CalendarIcon } from '@mui/x-date-pickers';

export function generatePages(projects: Project[], _cleaningPackages: any, projectId?: string) {
  return [
    {
      name: 'Plant',
      icon: <SolarPowerIcon />,
      section: 'location',
      roles: ['assessment'],
      description: (
        <InfoAlert severity="info">
          Define the project location and system type (fixed or tracker). This information is used to generate the Typical meteorological
          year (TMY).
        </InfoAlert>
      ),
      fields: [
        {
          type: 'group',
          label: 'Project',
          font_size: 'h5',
          fields: [
            {
              name: 'name',
              label: 'Project Name',
              type: 'text',
              size: 12,
              required: true,
            },
          ],
        },
        {
          type: 'group',
          label: 'Location',
          font_size: 'h5',
          fields: [
            {
              name: 'location',
              label: 'Example Plants',
              type: 'select',
              options: projects.map((project: Project) => {
                return { label: project.name, value: { ...project.location, name: project.name } };
              }),
            },
            {
              name: 'location.name',
              label: 'Location Name',
              type: 'text',
              size: 12,
              required: true,
            },
            {
              name: 'location.latitude',
              label: 'Latitude',
              type: 'number',
              size: 6,
              required: true,
            },
            {
              name: 'location.longitude',
              label: 'Longitude',
              type: 'number',
              size: 6,
              required: true,
            },
            {
              type: 'map',
              size: 12,
            },
          ],
        },
        {
          type: 'group',
          label: 'Mounting Structure',
          font_size: 'h5',
          fields: [
            {
              name: 'hsat',
              label: 'HSAT System',
              type: 'switch',
            },
            {
              label: 'Fixed Tilted System',
              type: 'group',
              isHidden: 'hsat',
              help: 'FixedTiltedSystemHelp',
              fields: [
                {
                  name: 'surface_tilt',
                  label: 'Tilt Angle (degrees)',
                  help: 'tiltAngle',
                  type: 'number',
                  size: 6,
                  required: true,
                },
              ],
            },
            {
              label: 'HSAT System',
              name: 'variable_tilt_angle',
              help: 'HSATsystemHelp',
              type: 'group',
              isHidden: 'hsat',
              revertHidden: true,
              fields: [
                {
                  name: 'tilt_angle.max_tilt_angle',
                  label: 'Maximum Feasible Angle (degrees)',
                  type: 'number',
                  help: 'MaxTiltAngle',
                  size: 6,
                  required: true,
                },
                {
                  name: 'tilt_angle.stow_angle',
                  label: 'Stow Position Angle (degrees)',
                  type: 'number',
                  help: 'StowAngleHelp',
                  size: 6,
                  required: true,
                },
                {
                  name: 'tilt_variation',
                  label: 'optimal tilt angle variation',
                  title: 'Tilt Angle Variation',
                  type: 'callContent',
                  url: 'tools/tilt-angle-variation',
                  params: ['location', 'tilt_angle'],
                  content: { component: 'chart', properties: { type: 'line', ylabel: 'Degrees', label: 'Tilt Angle [degrees]' } },
                  get_data_from_api: 'hsat',
                },
              ],
            },
          ],
        },
      ],
      default_values: {
        surface_tilt: 30,
        'tilt_angle.stow_angle': 45,
        'tilt_angle.max_tilt_angle': 60,
        variable_tilt_angle: false,
        'plant.tracker.table.module.width': 1.13,
        'plant.tracker.table.module.height': 2.25,
        'plant.tracker.table.module_orientation': 'landscape',
        'plant.tracker.table.module.capacity': 536,
        'plant.tracker.table.module.short_circuit_current': 14,
        'plant.tracker.table.module.isc_temp_coeff': 0.0004,
        'plant.tracker.table.module.pmax_temp_coeff': -0.0035,
        'plant.tracker.table.dimensions': [42, 2],
        'plant.ac_capacity': 90,
        'plant.dc_capacity': 100,
        'plant.grid_limit': 100,
        'plant.width': 600,
        'plant.tracker.num_tables': 3,
        use_plant_geometry: false,
        'plant.terrain_penalty_factor': 1,
      },
    },
    {
      name: 'Plant',
      icon: <SolarPowerIcon />,
      section: 'location',
      roles: ['mitigation_strategy', 'ex_post_analysis', 'operational_strategy'],
      description: (
        <InfoAlert severity="info">
          Define the project location, system type (fixed or tracker), and plant layout. This information is used to generate the Typical
          meteorological year (TMY), simulate energy yield with/without soiling, and estimate the distances that a potential cleaning system
          would need to cover.
        </InfoAlert>
      ),
      fields: [
        {
          type: 'group',
          label: 'Project',
          font_size: 'h5',
          fields: [
            {
              name: 'name',
              label: 'Project Name',
              type: 'text',
              size: 12,
              required: true,
            },
          ],
        },
        {
          type: 'group',
          label: 'Location',
          font_size: 'h5',
          fields: [
            {
              name: 'location',
              label: 'Example Plants',
              type: 'select',
              options: projects.map((project: Project) => {
                return { label: project.name, value: { ...project.location, name: project.name } };
              }),
            },
            {
              name: 'location.name',
              label: 'Location Name',
              type: 'text',
              size: 12,
              required: true,
            },
            {
              name: 'location.latitude',
              label: 'Latitude',
              type: 'number',
              size: 6,
              required: true,
            },
            {
              name: 'location.longitude',
              label: 'Longitude',
              type: 'number',
              size: 6,
              required: true,
            },
            {
              type: 'map',
              size: 12,
            },
          ],
        },
        {
          type: 'group',
          label: 'Mounting Structure',
          font_size: 'h5',
          fields: [
            {
              name: 'hsat',
              label: 'HSAT System',
              type: 'switch',
            },
            {
              label: 'Fixed Tilted System',
              type: 'group',
              isHidden: 'hsat',
              help: 'FixedTiltedSystemHelp',
              fields: [
                {
                  name: 'surface_tilt',
                  label: 'Tilt Angle (degrees)',
                  help: 'tiltAngle',
                  type: 'number',
                  size: 6,
                  required: true,
                },
              ],
            },
            {
              label: 'HSAT System',
              name: 'variable_tilt_angle',
              help: 'HSATsystemHelp',
              type: 'group',
              isHidden: 'hsat',
              revertHidden: true,
              fields: [
                {
                  name: 'tilt_angle.max_tilt_angle',
                  label: 'Maximum Feasible Angle (degrees)',
                  type: 'number',
                  help: 'MaxTiltAngle',
                  size: 6,
                  required: true,
                },
                {
                  name: 'tilt_angle.stow_angle',
                  label: 'Stow Position Angle (degrees)',
                  type: 'number',
                  help: 'StowAngleHelp',
                  size: 6,
                  required: true,
                },
                {
                  name: 'tilt_variation',
                  label: 'optimal tilt angle variation',
                  title: 'Tilt Angle Variation',
                  type: 'callContent',
                  url: 'tools/tilt-angle-variation',
                  params: ['location', 'tilt_angle'],
                  content: { component: 'chart', properties: { type: 'line', ylabel: 'Degrees', label: 'Tilt angle [degrees]' } },
                  get_data_from_api: 'hsat',
                },
              ],
            },
          ],
        },
        {
          type: 'group',
          label: 'Configuration',
          font_size: 'h5',
          fields: [
            {
              label: 'Module',
              type: 'group',
              fields: [
                {
                  name: 'plant.tracker.table.module.capacity',
                  label: 'Capacity (Wp)',
                  type: 'number',
                  size: 4,
                  required: true,
                },
                {
                  name: 'plant.tracker.table.module.width',
                  label: 'Width (m)',
                  type: 'number',
                  size: 4,
                  required: true,
                },
                {
                  name: 'plant.tracker.table.module.height',
                  label: 'Height (m)',
                  type: 'number',
                  size: 4,
                  required: true,
                },
                {
                  name: 'plant.tracker.table.module.short_circuit_current',
                  label: 'Short circuit Current (A)',
                  type: 'number',
                  size: 4,
                  required: true,
                  roles: ['ex_post_analysis', 'operational_strategy'],
                },
                {
                  name: 'plant.tracker.table.module.isc_temp_coeff',
                  label: 'Isc temperature coefficient α (1/°C)',
                  help: 'iscTempCoeff',
                  type: 'number',
                  size: 4,
                  required: true,
                  roles: ['ex_post_analysis', 'operational_strategy'],
                },
                {
                  name: 'plant.tracker.table.module.pmax_temp_coeff',
                  label: 'Pmax temperature coefficient β (1/°C)',
                  help: 'pmaxTempCoeff',
                  type: 'number',
                  size: 4,
                  required: true,
                  roles: ['ex_post_analysis', 'operational_strategy'],
                },
              ],
            },
            {
              type: 'group',
              label: 'Table',
              help: 'plantDimensionsHelp',
              fields: [
                {
                  name: 'plant.tracker.table.module_orientation',
                  label: 'Module Orientation',
                  help: 'orientation',
                  type: 'select',
                  options: [
                    {
                      value: 'portrait',
                      label: 'Portrait',
                    },
                    {
                      value: 'landscape',
                      label: 'Landscape',
                    },
                  ],
                  size: 6,
                  required: true,
                },
                {
                  name: 'plant.tracker.table.dimensions',
                  label: 'Table Dimensions',
                  type: 'special_array',
                  size: 12,
                  items: [
                    {
                      name: 'hor',
                      label: 'Number of modules per table horizontally',
                      type: 'number',
                      size: 6,
                      required: true,
                    },
                    {
                      name: 'vert',
                      label: 'Number of modules per table vertically',
                      type: 'number',
                      size: 6,
                      required: true,
                    },
                  ],
                  required: true,
                },
              ],
            },
            {
              type: 'group',
              label: 'Tracker',
              fields: [
                {
                  name: 'plant.tracker.num_tables',
                  label: 'Number of tables per tracker',
                  type: 'number',
                  size: 6,
                  required: true,
                },
              ],
            },
            {
              type: 'group',
              label: 'Plant Details',
              // help: 'table',
              fields: [
                {
                  name: 'plant.ac_capacity',
                  label: 'Plant Capacity AC (MWac)',
                  type: 'number',
                  size: 6,
                  required: true,
                },
                {
                  name: 'plant.terrain_penalty_factor',
                  help: 'TerrainPenaltyFactor',
                  label: 'Terrain Penalty Factor',
                  type: 'number',
                  size: 6,
                  required: true,
                },
                {
                  name: 'plant.grid_limit',
                  label: 'Grid Limit (MWac)',
                  type: 'number',
                  size: 6,
                  required: true,
                },
                {
                  name: 'use_plant_geometry',
                  label: 'Use plant geometry',
                  type: 'switch',
                },
                {
                  name: 'plant.dc_capacity',
                  label: 'Plant Capacity DC (MWp)',
                  isHidden: 'use_plant_geometry',
                  type: 'number',
                  size: 6,
                  required: true,
                },
                {
                  name: 'plant.width',
                  label: 'Average Plant Width (m)',
                  isHidden: 'use_plant_geometry',
                  help: 'plantWidth',
                  type: 'number',
                  size: 6,
                  required: true,
                },
                {
                  name: 'plant.row_length',
                  label: 'Row length (m)',
                  help: 'RowLength',
                  isHidden: 'use_plant_geometry',
                  revertHidden: true,
                  type: 'number',
                  size: 6,
                  required: true,
                },
                {
                  name: 'plant.number_of_rows',
                  label: 'Number of Rows',
                  isHidden: 'use_plant_geometry',
                  revertHidden: true,
                  type: 'number',
                  size: 6,
                  required: true,
                },
              ],
            },
            {
              type: 'group',
              label: 'Plant Dimensions',
              // help: 'table',
              fields: [
                {
                  name: 'plant_dimensions',
                  label: 'Plant Dimension Content',
                  type: 'callContent',
                  method: 'post',
                  url: 'tools/plant-info',
                  params: [],
                  body: ['plant', 'use_plant_geometry'],
                  pattern: 'rain_pattern',
                  content: { component: 'info', properties: {} },
                },
              ],
            },
          ],
        },
      ],
      default_values: {
        surface_tilt: 30,
        'tilt_angle.stow_angle': 45,
        'tilt_angle.max_tilt_angle': 60,
        variable_tilt_angle: false,
        'plant.tracker.table.module.width': 1.13,
        'plant.tracker.table.module.height': 2.25,
        'plant.tracker.table.module_orientation': 'landscape',
        'plant.tracker.table.module.capacity': 536,
        'plant.tracker.table.module.short_circuit_current': 14,
        'plant.tracker.table.module.isc_temp_coeff': 0.0004,
        'plant.tracker.table.module.pmax_temp_coeff': -0.0035,
        'plant.tracker.table.dimensions': [42, 2],
        'plant.ac_capacity': 90,
        'plant.dc_capacity': 100,
        'plant.grid_limit': 100,
        'plant.width': 600,
        'plant.tracker.num_tables': 3,
        use_plant_geometry: false,
        'plant.terrain_penalty_factor': 1,
      },
    },
    {
      name: 'Meteorological Conditions',
      icon: <CloudIcon />,
      section: 'meteo',
      roles: ['mitigation_strategy', 'assessment'],
      prerequisites: [],
      description: (
        <InfoAlert severity="info">
          The page displays the values from the generated TMY, which are editable. Since rainfall affects natural PV panel cleaning, you
          must define a rain threshold and a rain cleaning efficiency.
        </InfoAlert>
      ),
      fields: [
        {
          label: 'Rain',
          font_size: 'h5',
          type: 'group',
          hidden: false,
          fields: [
            {
              label: 'Rain Pattern',
              type: 'group',
              fields: [
                {
                  name: 'rain.from_api',
                  label: 'Use rain data from external source',
                  help: 'externalRainPattern',
                  type: 'switch',
                },
                {
                  name: 'rain.rain_pattern.filename',
                  label: 'Data file',
                  type: 'text',
                  isHidden: 'rain.from_api',
                  disabled: 'true',
                },
                {
                  name: 'rain.rain_pattern',
                  label: 'NASA Rain Pattern',
                  title: 'Rain Pattern',
                  type: 'callContent',
                  url: 'pattern/rain',
                  params: ['location', 'rain_threshold'],
                  content: { component: 'chart', properties: { type: 'column', ylabel: 'mm/day', label: 'Precipitation [mm/day]' } },
                  get_data_from_api: 'rain.from_api',
                  upload: {
                    name: 'rain_pattern_upload',
                    target: 'rain.rain_pattern',
                    label: 'Upload Rain Pattern CSV',
                    type: 'upload',
                    disabled: 'rain.from_api',
                  },
                },
              ],
            },
            {
              label: 'Rain Threshold',
              type: 'group',
              fields: [
                {
                  name: 'rain_threshold',
                  label: 'Rain Threshold (mm/day)',
                  help: 'rainThreshold',
                  type: 'number',
                  size: 6,
                  required: true,
                },
              ],
            },
            {
              label: 'Rain Cleaning Efficiency',
              type: 'group',
              fields: [
                {
                  name: 'rain.compute_rain_efficiency',
                  type: 'switch',
                  help: 'computedRainCleaning',
                  label: 'Variable cleaning efficiency',
                },
                {
                  name: 'rain.rain_efficiency',
                  label: 'Rain Cleaning Efficiency (0-1)',
                  help: 'rainEfficiency',
                  type: 'number',
                  size: 6,
                  required: true,
                  isHidden: 'rain.compute_rain_efficiency',
                },
                {
                  type: 'group',
                  label: 'Rain Cleaning Efficiency',
                  font_size: 'h8',
                  help: 'computedRainEfficiency',
                  isHidden: 'rain.compute_rain_efficiency',
                  revertHidden: true,
                  fields: [
                    {
                      name: 'computed_rain_efficiency',
                      label: 'computed rain efficiency based on soiling ratio',
                      type: 'callContent',
                      method: 'post',
                      url: 'tools/rain-efficiency',
                      params: ['location', 'tilt_angle', 'surface_tilt', 'rain_threshold', 'variable_tilt_angle'],
                      body: ['rain'],
                      pattern: 'rain_pattern',
                      content: { component: 'chart', properties: { type: 'line', ylabel: 'Rain Efficiency', label: 'Rain Efficiency' } },
                      get_data_from_api: 'rain.compute_rain_efficiency',
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          label: 'Wind Speed',
          type: 'group',
          font_size: 'h5',
          fields: [
            {
              name: 'wind_speed.from_api',
              label: 'Use wind speed data from external source',
              help: 'externalWindSpeed',
              type: 'switch',
            },
            {
              name: 'wind_speed.pattern.filename',
              label: 'Data file',
              type: 'text',
              isHidden: 'wind_speed.from_api',
              disabled: 'true',
            },
            {
              name: 'wind_speed.pattern',
              label: 'NASA Wind Speed Pattern',
              title: 'Wind Speed Pattern',
              type: 'callContent',
              url: 'pattern/wind-speed',
              params: ['location', 'rain_threshold'],
              content: { component: 'chart', properties: { type: 'column', ylabel: 'm/s', label: 'Wind Speed [m/s]' } },
              get_data_from_api: 'wind_speed.from_api',
              upload: {
                name: 'wind_speed_pattern_upload',
                target: 'wind_speed.pattern',
                label: 'Upload Wind Speed Pattern CSV',
                type: 'upload',
                disabled: 'wind_speed.from_api',
              },
            },
          ],
        },
        {
          label: 'Humidity',
          type: 'group',
          font_size: 'h5',
          fields: [
            {
              name: 'relative_humidity.from_api',
              label: 'Use relative humidity data from external source',
              help: 'externalRelativeHumidity',
              type: 'switch',
            },
            {
              name: 'relative_humidity.pattern.filename',
              label: 'Data file',
              type: 'text',
              isHidden: 'relative_humidity.from_api',
              disabled: 'true',
            },
            {
              name: 'relative_humidity.pattern',
              label: 'NASA Relative Humidity Pattern',
              title: 'Relative Humidity Pattern',
              type: 'callContent',
              url: 'pattern/relative-humidity',
              params: ['location', 'rain_threshold'],
              content: { component: 'chart', properties: { type: 'column', ylabel: '%', label: 'Relative Humidity [%]' } },
              get_data_from_api: 'relative_humidity.from_api',
              upload: {
                name: 'relative_humidity_pattern_upload',
                target: 'relative_humidity.pattern',
                label: 'Upload Relative Humidity Pattern CSV',
                type: 'upload',
                disabled: 'relative_humidity.from_api',
              },
            },
          ],
        },
      ],
      default_values: {
        yearly_cleaning_days: [],
        'rain.rain_efficiency': 0.95,
        rain_threshold: 2,
        'rain.compute_rain_efficiency': false,
        'rain.from_api': true,
        date: '01/01/1900',
        'wind_speed.from_api': true,
        'relative_humidity.from_api': true,
      },
    },
    {
      name: 'Meteorological Conditions Operations',
      icon: <CloudIcon />,
      section: 'meteo2',
      roles: ['ex_post_analysis', 'operational_strategy'],
      prerequisites: [],
      description: (
        <InfoAlert severity="info">
          This page displays the available weather data for the selected location, which you can modify as needed. If no data is currently
          shown, you may still complete the template CSV using your own local measurements. Please follow the specific instructions provided
          in each section. Please note:
          <br />
          <ul>
            <li>By default, a Typical Meteorological Year (TMY) is proposed.</li>
            <li>You are free to upload your own recent weather data for the plant's location.</li>
            <li>
              The uploaded dataset may cover less or more than one year. However, ensure that the dates aligns with those of the final
              soiling ratio curve selected in the following tabs.
            </li>
            <li>
              If the dates do not match, the system will automatically replace or supplement your data with NASA data to ensure consistency
              with the timeframe of the chosen soiling ratio curve.
            </li>
          </ul>
        </InfoAlert>
      ),
      fields: [
        {
          label: 'Rain',
          font_size: 'h5',
          type: 'group',
          hidden: false,
          fields: [
            {
              label: 'Rain Pattern',
              type: 'group',
              fields: [
                {
                  name: 'rain2.from_api',
                  label: 'Use rain data from external source',
                  help: 'externalRainPattern2',
                  type: 'switch',
                },
                {
                  name: 'rain2.pattern.filename',
                  label: 'Data file',
                  type: 'text',
                  isHidden: 'rain2.from_api',
                  disabled: 'true',
                },
                {
                  name: 'rain2.rain_pattern',
                  label: 'NASA Rain Pattern',
                  title: 'Rain Pattern',
                  type: 'callContent',
                  url: 'pattern/rain',
                  params: ['location', 'rain_threshold'],
                  content: { component: 'chart', properties: { type: 'column', ylabel: 'mm/day', label: 'Precipitation [mm/day]' } },
                  get_data_from_api: 'rain2.from_api',
                  upload: {
                    name: 'rain_pattern_upload2',
                    target: 'rain2.rain_pattern',
                    label: 'Upload Rain Pattern CSV',
                    type: 'upload',
                    disabled: 'rain2.from_api',
                  },
                },
              ],
            },
            {
              label: 'Rain Threshold',
              type: 'group',
              fields: [
                {
                  name: 'rain_threshold',
                  label: 'Rain Threshold (mm/day)',
                  help: 'rainThreshold',
                  type: 'number',
                  size: 6,
                  required: true,
                },
              ],
            },
            {
              label: 'Rain Cleaning Efficiency',
              type: 'group',
              fields: [
                {
                  name: 'rain.compute_rain_efficiency',
                  type: 'switch',
                  help: 'computedRainCleaning',
                  label: 'Variable cleaning efficiency',
                },
                {
                  name: 'rain.rain_efficiency',
                  label: 'Rain Cleaning Efficiency (0-1)',
                  help: 'rainEfficiency',
                  type: 'number',
                  size: 6,
                  required: true,
                  isHidden: 'rain.compute_rain_efficiency',
                },
                {
                  type: 'group',
                  label: 'Rain Cleaning Efficiency',
                  font_size: 'h8',
                  help: 'computedRainEfficiency',
                  isHidden: 'rain.compute_rain_efficiency',
                  revertHidden: true,
                  fields: [
                    {
                      name: 'computed_rain_efficiency2',
                      label: 'computed rain efficiency based on soiling ratio',
                      type: 'callContent',
                      method: 'post',
                      url: 'tools/rain-efficiency',
                      params: ['location', 'tilt_angle', 'surface_tilt', 'rain_threshold', 'variable_tilt_angle'],
                      body: ['rain2'],
                      pattern: 'rain_pattern',
                      content: { component: 'chart', properties: { type: 'line', ylabel: 'Rain Efficiency', label: 'Rain Efficiency' } },
                      get_data_from_api: 'rain.compute_rain_efficiency',
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          label: 'Wind Speed',
          type: 'group',
          font_size: 'h5',
          fields: [
            {
              name: 'wind_speed2.from_api',
              label: 'Use wind speed data from external source',
              help: 'externalWindSpeed2',
              type: 'switch',
            },
            {
              name: 'wind_speed2.pattern.filename',
              label: 'Data file',
              type: 'text',
              isHidden: 'wind_speed2.from_api',
              disabled: 'true',
            },
            {
              name: 'wind_speed2.pattern',
              label: 'NASA Wind Speed Pattern',
              title: 'Wind Speed Pattern',
              type: 'callContent',
              url: 'pattern/wind-speed',
              params: ['location', 'rain_threshold'],
              content: { component: 'chart', properties: { type: 'column', ylabel: 'm/s', label: 'Wind Speed [m/s]' } },
              get_data_from_api: 'wind_speed2.from_api',
              upload: {
                name: 'wind_speed_pattern_upload2',
                target: 'wind_speed2.pattern',
                label: 'Upload Wind Speed Pattern CSV',
                type: 'upload',
                disabled: 'wind_speed2.from_api',
              },
            },
          ],
        },
        {
          label: 'Humidity',
          type: 'group',
          font_size: 'h5',
          fields: [
            {
              name: 'relative_humidity2.from_api',
              label: 'Use relative humidity data from external source',
              help: 'externalRelativeHumidity2',
              type: 'switch',
            },
            {
              name: 'relative_humidity2.pattern.filename',
              label: 'Data file',
              type: 'text',
              isHidden: 'relative_humidity2.from_api',
              disabled: 'true',
            },
            {
              name: 'relative_humidity2.pattern',
              label: 'NASA Relative Humidity Pattern',
              title: 'Relative Humidity Pattern',
              type: 'callContent',
              url: 'pattern/relative-humidity',
              params: ['location', 'rain_threshold'],
              content: { component: 'chart', properties: { type: 'column', ylabel: '%', label: 'Relative Humidity [%]' } },
              get_data_from_api: 'relative_humidity2.from_api',
              upload: {
                name: 'relative_humidity_pattern_upload2',
                target: 'relative_humidity2.pattern',
                label: 'Upload Relative Humidity Pattern CSV',
                type: 'upload',
                disabled: 'relative_humidity2.from_api',
              },
            },
          ],
        },
      ],
      default_values: {
        yearly_cleaning_days: [],
        'rain.rain_efficiency': 0.95,
        rain_threshold: 2,
        'rain.compute_rain_efficiency': false,
        'rain2.from_api': true,
        date: '01/01/1900',
        'wind_speed2.from_api': true,
        'relative_humidity2.from_api': true,
      },
    },
    {
      name: 'Soiling Sensor Data',
      icon: <TrendingDownOutlinedIcon />,
      section: 'soiling_sensor',
      roles: ['ex_post_analysis', 'operational_strategy'],
      prerequisites: [],
      description: (
        <InfoAlert severity="info">
          Upload your available site-specific data from soiling sensors on this page. The tool is compatible with data from RDE300 and
          DustIQ. If you're using other sensors, you will need to directly provide the soiling ratio. Use the <b>Add new block</b> button to
          upload data from as many sensors or soiling ratio datasets you may have.
          <br />
          <br />
          The instantaneous measurements are first filtered to remove outliers and exclude data collected under low or intermittent
          irradiance conditions. Then, a daily soiling ratio is computed using the irradiance-weighted average of the filtered data. On this
          page you see the daily soiling ratio.
        </InfoAlert>
      ),
      fields: [
        {
          label: 'Soiling Sensor Data',
          name: 'soiling_sensor_data',
          font_size: 'h5',
          type: 'group',
          size: 12,
          help: 'sensorDataHelp',
          fields: [
            {
              name: 'soiling_sensor_data.name',
              label: 'Sensor Name',
              type: 'text',
              size: 6,
              required: true,
              placeholder: 'Enter a name for this sensor (e.g., "Sensor A", "Main Sensor")',
            },
            {
              name: 'soiling_sensor_data.type',
              label: 'Sensor Type',
              type: 'select',
              size: 6,
              required: true,
              options: [
                { label: 'RDE300', value: 'rde300' },
                { label: 'Soiling Ratio', value: 'soiling_ratio' },
                { label: 'DustIQ', value: 'dustiq' },
              ],
              default: 'rde300',
            },
            {
              name: 'soiling_sensor_data.pattern',
              label: 'Sensor Pattern',
              title: 'Sensor Pattern',
              type: 'callContent',
              data_type: 'soiling_sensor_data',
              content: {
                component: 'chart',
                properties: { type: 'line', ylabel: 'Value', label: 'Sensor', ymin: 0.0, ymax: 1.2 },
              },
              upload: {
                name: 'soiling_sensor_data_pattern_upload',
                target: 'soiling_sensor_data.pattern',
                label: 'Upload Sensor Pattern CSV',
                type: 'upload',
                params: {
                  project_id: projectId,
                  latitude: 'location.latitude',
                  longitude: 'location.longitude',
                },
              },
            },
          ],
        },
      ],
      default_values: {},
    },
    {
      name: 'Soiling Ratio (Ex-Post)',
      icon: <TrendingDownOutlinedIcon />,
      section: 'soiling',
      roles: ['ex_post_analysis'],
      prerequisites: [],
      description: (
        <InfoAlert severity="info">
          This page displays the <b>processed data</b> uploaded from your soiling sensors.
        </InfoAlert>
      ),
      fields: [
        {
          label: 'Soiling Ratio Pattern',
          name: 'soiling_ratio_input',
          font_size: 'h5',
          type: 'group',
          size: 12,
          help: 'soilingRatioPatternHelp',
          fields: [
            {
              name: 'soiling_ratio_operational_ex_post.pattern',
              label: 'Soiling Ratio Pattern',
              title: '',
              type: 'callContent',
              url: 'soiling-ratios',
              method: 'post',
              body: ['rain'],
              pattern: 'rain_pattern',
              params: [
                'location',
                'rain_threshold',
                'tilt_angle',
                'surface_tilt',
                'variable_tilt_angle',
                'rain.rain_efficiency',
                'rain.compute_rain_efficiency',
              ],
              content: {
                component: 'chart',
                properties: { type: 'line', ylabel: 'Soiling Ratio', label: 'Soiling Ratio', ymin: 0.4, ymax: 1.2 },
              },
              get_data_from_api: 'soiling_ratio_operational_ex_post.from_api',
            },
          ],
        },
        {
          label: 'Combine Soiling Ratio Curves',
          name: 'soiling_ratio_combine',
          font_size: 'h5',
          type: 'group',
          size: 12,
          help: 'combineSoilingCurvesExPost',
          fields: [
            {
              name: 'soiling_ratio_operational_ex_post_combine',
              label: 'Combine Curves',
              type: 'soilingRatioMultiSelect',
              size: 12,
            },
          ],
        },
      ],
      default_values: {
        'soiling_ratio_operational_ex_post.from_api': true,
      },
    },
    {
      name: 'Soiling Ratio',
      icon: <TrendingDownOutlinedIcon />,
      section: 'soiling',
      roles: ['operational_strategy'],
      prerequisites: [],
      description: (
        <InfoAlert severity="info">
          This page displays the <b>processed data</b> uploaded from your soiling sensors.
        </InfoAlert>
      ),
      fields: [
        {
          label: 'Soiling Ratio Pattern',
          name: 'soiling_ratio_input',
          font_size: 'h5',
          type: 'group',
          size: 12,
          help: 'soilingRatioPatternHelp',
          fields: [
            {
              name: 'soiling_ratio_operational.pattern',
              label: 'Soiling Ratio Pattern',
              title: '',
              type: 'callContent',
              url: 'soiling-ratios',
              method: 'post',
              body: ['rain', 'plant', 'use_plant_geometry'],
              pattern: 'rain_pattern',
              params: [
                'location',
                'rain_threshold',
                'tilt_angle',
                'surface_tilt',
                'variable_tilt_angle',
                'rain.rain_efficiency',
                'rain.compute_rain_efficiency',
              ],
              content: {
                component: 'chart',
                properties: { type: 'line', ylabel: 'Soiling Ratio', label: 'Soiling Ratio', ymin: 0.4, ymax: 1.2 },
              },
              get_data_from_api: 'soiling_ratio_operational.from_api',
            },
          ],
        },
        {
          label: 'Combine Soiling Ratio Curves',
          name: 'soiling_ratio_combine',
          font_size: 'h5',
          type: 'group',
          size: 12,
          help: 'combineSoilingCurvesOperational',
          fields: [
            {
              name: 'soiling_ratio_operational_combine',
              label: 'Combine Curves',
              type: 'soilingRatioMultiSelect',
              size: 12,
            },
          ],
        },
      ],
      default_values: {
        'soiling_ratio_operational.from_api': true,
      },
    },
    {
      name: 'Soiling Ratio',
      icon: <TrendingDownOutlinedIcon />,
      section: 'soiling',
      roles: ['assessment', 'mitigation_strategy'],
      prerequisites: [],
      description: (
        <InfoAlert severity="info">
          This page shows a Typical Soiling Year (TSY) estimated with the particulate matter data and rainfall data from the TMY. Results
          can be exported or replaced with site-specific operational data.
        </InfoAlert>
      ),
      fields: [
        {
          label: 'Soiling Ratio Pattern',
          name: 'soiling_ratio_input',
          font_size: 'h5',
          type: 'group',
          size: 12,
          fields: [
            {
              name: 'soiling_ratio.from_api',
              label: 'Use soiling ratio external source',
              help: 'externalSoilingRatio',
              type: 'switch',
            },
            {
              name: 'soiling_ratio.pattern.filename',
              label: 'Data file',
              type: 'text',
              isHidden: 'soiling_ratio.from_api',
              disabled: 'true',
            },
            {
              name: 'soiling_ratio.pattern',
              label: 'Soiling Ratio Pattern',
              title: 'Soiling Ratio Pattern',
              type: 'callContent',
              url: 'soiling-ratio',
              method: 'post',
              body: ['rain'],
              pattern: 'rain_pattern',
              params: [
                'location',
                'rain_threshold',
                'tilt_angle',
                'surface_tilt',
                'variable_tilt_angle',
                'rain.rain_efficiency',
                'rain.compute_rain_efficiency',
              ],
              content: {
                component: 'chart',
                properties: { type: 'line', ylabel: 'Soiling Ratio', label: 'Soiling Ratio', ymin: 0.4, ymax: 1.2 },
              },
              get_data_from_api: 'soiling_ratio.from_api',
              upload: {
                name: 'soiling_ratio_pattern_upload',
                target: 'soiling_ratio.pattern',
                label: 'Upload Soiling Ratio Pattern CSV',
                help: 'userSoilingRatio',
                type: 'upload',
                disabled: 'soiling_ratio.from_api',
                params: { normalized: true },
              },
            },
          ],
        },
      ],
      default_values: {
        'soiling_ratio.from_api': true,
      },
    },
    {
      name: 'Human Cleaning',
      icon: <CleaningServicesIcon />,
      section: 'human_cleaning',
      roles: ['assessment'],
      prerequisites: [],
      description: (
        <InfoAlert severity="info">
          This page allows the input of cleaning dates and their efficiency. This data is used to generate the soiling ratio time series
          shown in the Soiling Assessment page.
        </InfoAlert>
      ),
      fields: [
        {
          label: 'Human Cleaning',
          type: 'group',
          font_size: 'h5',
          fields: [
            {
              label: 'Cleaning Efficiency',
              type: 'group',
              fields: [
                {
                  name: 'cleaning_efficiency',
                  label: 'Cleaning Efficiency',
                  help: 'cleaningEfficiency',
                  type: 'number',
                  size: 12,
                  required: true,
                },
              ],
            },
            {
              label: 'Cleaning Dates',
              type: 'group',
              fields: [
                {
                  name: 'yearly_cleaning_days',
                  type: 'list',
                  size: 6,
                  fields: [
                    {
                      name: 'date',
                      label: 'Cleaning Date',
                      type: 'date',
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
      default_values: {
        yearly_cleaning_days: [],
        cleaning_efficiency: 0.9,
        date: '01/01/1900',
      },
    },
    {
      name: 'Yield Evaluation',
      icon: <WbSunnyIcon />,
      section: 'yield',
      roles: ['mitigation_strategy', 'ex_post_analysis', 'operational_strategy'],
      prerequisites: [],
      description: (
        <InfoAlert severity="info">
          This page displays the monthly energy production simulated based on the plant configuration entered. Results can be manually
          modified if there are more accurate or site-specific data.
        </InfoAlert>
      ),
      fields: [
        {
          type: 'group',
          label: 'Yield Evaluation',
          help: 'yieldSelect',
          font_size: 'h5',
          fields: [
            {
              name: 'yield_evaluation.source',
              label: 'Yield Data Source',
              type: 'buttonGroup',
              options: [
                {
                  value: 'api',
                  label: 'Use production data from TMY solar irradiance database',
                  help: 'yieldApiHelp',
                },
                {
                  value: 'csv',
                  label: 'Upload hourly DC production data',
                  help: 'yieldCsvHelp',
                },
                {
                  value: 'monthly',
                  label: 'Upload monthly AC production data',
                  help: 'yieldMonthlyHelp',
                },
              ],
            },
            {
              name: 'yield_evaluation.monthly_values',
              label: 'Monthly Yield Values (MWh)',
              type: 'monthlyValues',
              disabled: 'yield_evaluation.source', // This line must be exactly like this
              isHidden: 'yield_evaluation.source',
            },
            {
              name: 'yield_evaluation.pattern',
              type: 'callContent',
              label: 'Yield Evaluation',
              title: 'Yield Evaluation',
              url: 'tools/yield-evaluation',
              method: 'post',
              params: [
                'location',
                'tilt_angle',
                'surface_tilt',
                'rain_threshold',
                'variable_tilt_angle',
                'rain.rain_efficiency',
                'rain.compute_rain_efficiency',
              ],
              body: ['plant', 'use_plant_geometry', 'yield_evaluation.pattern'],
              pattern: 'yield_pattern',
              content: {
                component: 'chart',
                properties: { type: 'line', ylabel: 'MWh', label: 'Yield [MWh]' },
              },
              get_data_from_api: 'yield_evaluation.source',
              upload: {
                name: 'yield_evaluation_pattern_upload',
                target: 'yield_evaluation.pattern',
                label: 'Upload Yield Evaluation Pattern CSV',
                type: 'upload',
                help: 'yieldUpload',
                disabled: 'yield_evaluation.source', // The button will be disabled if source has a value
                isHidden: 'yield_evaluation.source', // Hide upload when monthly is selected
                params: { normalized: false, freq: 'h' },
              },
            },
          ],
        },
      ],
      default_values: {
        use_plant_geometry: false,
        'yield_evaluation.source': 'api',
        'yield_evaluation.monthly_values': Array(12).fill(0),
      },
    },
    {
      name: 'Financial Parameters',
      icon: <AttachMoneyIcon />,
      section: 'financials',
      roles: ['mitigation_strategy', 'ex_post_analysis', 'operational_strategy'],
      description: (
        <InfoAlert severity="info">
          Specify the financial parameters used to estimate the cost and benefit of mitigation activities and to project the future cash
          flows of the mitigation strategy.
        </InfoAlert>
      ),
      fields: [
        {
          type: 'group',
          label: 'Consumables',
          help: 'Consumables',
          font_size: 'h5',
          fields: [
            {
              name: 'financial.water_price',
              label: 'Water Price ($/m³)',
              type: 'number',
              size: 6,
              required: true,
            },
            {
              name: 'financial.fuel_price',
              label: 'Fuel Price ($/L)',
              type: 'number',
              size: 6,
              required: true,
            },
          ],
        },
        {
          type: 'group',
          label: 'Workers',
          font_size: 'h5',
          fields: [
            {
              name: 'workers.wage',
              label: 'Worker Wage ($/hour)',
              type: 'number',
              size: 4,
              required: true,
            },
            {
              name: 'workers.shift_duration',
              label: 'Shift Duration (hours)',
              type: 'number',
              size: 4,
              required: true,
            },
            {
              name: 'workers.working_days_per_week',
              label: 'Working Days per Week',
              type: 'number',
              size: 4,
              required: true,
            },
            {
              name: 'workers.not_working_hours_per_day',
              label: 'Not Working Hours per Shift',
              type: 'number',
              size: 4,
              required: true,
            },
            {
              name: 'workers.shifts_per_day',
              label: 'Shifts per Day',
              type: 'number',
              size: 4,
              required: true,
            },
          ],
        },
        {
          type: 'group',
          label: 'Optimization',
          font_size: 'h5',
          fields: [
            {
              name: 'financial.plant_lifetime',
              label: 'Optimization Horizon (years)',
              type: 'number',
              size: 6,
              required: true,
              roles: ['mitigation_strategy', 'operational_strategy'],
            },
            {
              name: 'financial.discount_rate',
              label: 'Discount Rate (%)',
              help: 'discount_rate',
              type: 'number',
              size: 6,
              required: true,
            },
            {
              name: 'financial.market_price',
              label: 'Market Price ($/MWh)',
              help: 'market_price',
              type: 'number',
              size: 6,
              required: true,
            },
          ],
        },
      ],
      default_values: {
        'financial.plant_lifetime': 30,
        'financial.discount_rate': 8,
        'financial.water_price': 8,
        'financial.fuel_price': 1.3,
        'workers.shift_duration': 8,
        'workers.working_days_per_week': 5,
        'workers.not_working_hours_per_day': 1,
        'workers.wage': 8,
        'workers.shifts_per_day': 1,
        'financial.market_price': 47,
      },
    },
    {
      name: 'Cleaning Schedule',
      icon: <CalendarIcon />,
      section: 'cleaning_schedule',
      roles: ['mitigation_strategy', 'ex_post_analysis', 'operational_strategy'],
      description: {
        mitigation_strategy: (
          <InfoAlert severity="info">Complete here any constraints to take into account in the Mitigation Strategy.</InfoAlert>
        ),
        ex_post_analysis: (
          <InfoAlert severity="info">
            Complete the information about your current cleaning strategy by indicating the dates when cleanings were performed at your
            plant, their duration and efficiency.
          </InfoAlert>
        ),
        operational_strategy: (
          <InfoAlert severity="info">
            Complete the information about your current cleaning solution by indicating the their duration and efficiency. Blacklist days
            and special cleaning event can also be indicated.
          </InfoAlert>
        ),
      },
      fields: [
        {
          type: 'group',
          label: 'Current Cleaning Solution',
          font_size: 'h5',
          roles: ['ex_post_analysis', 'operational_strategy'],
          fields: [
            {
              name: 'current_efficiency',
              label: 'Cleaning Efficiency',
              help: 'cleaningEfficiency',
              type: 'number',
              size: 4,
            },
            {
              name: 'current_days_to_clean',
              label: 'Days To Clean',
              type: 'number',
              size: 4,
              help: 'daysToClean',
            },
            {
              name: 'current_cleaning_opex',
              label: 'OPEX Cost Per Cleaning',
              type: 'number',
              size: 4,
            },
            {
              name: 'current_cleaning_dates',
              type: 'list',
              size: 6,
              fields: [
                {
                  name: 'date',
                  label: 'Cleaning Date',
                  type: 'date',
                  format: 'YYYY-MM-DD',
                  help: 'currentCleaningDates',
                },
              ],
              roles: ['ex_post_analysis'],
            },
          ],
        },
        {
          type: 'group',
          label: 'Blacklist days',
          font_size: 'h5',
          help: 'blacklistDays',
          fields: [
            {
              name: 'blacklist_dates',
              type: 'list',
              size: 6,
              fields: [
                {
                  name: 'date',
                  label: 'Blacklist Date',
                  type: 'date',
                },
              ],
            },
          ],
        },
        {
          type: 'group',
          label: 'Special cleaning events (migitation)',
          font_size: 'h5',
          help: 'specialCleaningEventsMitigation',
          roles: ['mitigation_strategy'],
          fields: [
            {
              name: 'special_efficiency_mitigation',
              label: 'Cleaning Efficiency',
              help: 'cleaningEfficiency',
              type: 'number',
              size: 4,
            },
            {
              name: 'special_days_to_clean_mitigation',
              label: 'Days To Clean',
              type: 'number',
              size: 4,
              help: 'daysToClean',
            },
            {
              name: 'special_cleaning_dates_mitigation',
              type: 'list',
              size: 6,
              fields: [
                {
                  name: 'date',
                  label: 'Cleaning Dates',
                  type: 'date',
                },
              ],
            },
          ],
        },
        {
          type: 'group',
          label: 'Special cleaning events (operational)',
          font_size: 'h5',
          help: 'specialCleaningEventsOperational',
          roles: ['operational_strategy'],
          fields: [
            {
              name: 'special_efficiency_operational',
              label: 'Cleaning Efficiency',
              help: 'cleaningEfficiency',
              type: 'number',
              size: 4,
            },
            {
              name: 'special_days_to_clean_operational',
              label: 'Days To Clean',
              type: 'number',
              size: 4,
              help: 'daysToClean',
            },
            {
              name: 'special_cleaning_dates_operational',
              type: 'list',
              size: 6,
              fields: [
                {
                  name: 'date',
                  label: 'Cleaning Dates',
                  type: 'date',
                  format: 'YYYY-MM-DD',
                },
              ],
            },
          ],
        },
      ],
      default_values: {
        current_efficiency: 0.95,
        current_days_to_clean: 21,
        current_cleaning_opex: 15000,
        special_efficiency_mitigation: 0.95,
        special_days_to_clean_mitigation: 21,
        special_efficiency_operational: 0.95,
        special_days_to_clean_operational: 21,
      },
    },
    {
      name: 'Cleaning Packages',
      icon: <CleaningServicesIcon />,
      section: 'cleaning_packages',
      roles: ['mitigation_strategy', 'operational_strategy'],
      description: (
        <InfoAlert severity="info">
          Select, or duplicate any cleaning package to be assessed. To edit a cleaning package, you first need to duplicate it.
        </InfoAlert>
      ),
      fields: [
        {
          type: 'group',
          label: 'Select Cleaning Packages',
          font_size: 'h5',
          fields: [
            {
              name: 'cleaning_package',
              label: 'Cleaning Packages',
              type: 'multiSelectSpecial',
              size: 12,
              options: 'cleaning_packages',
            },
          ],
        },
      ],
      default_values: {
        cleaning_package: {},
      },
    },
  ];
}
